import {AuthorizationSilo} from "@common/models/auth/authorization-silo"

/**
 * Returns organizationId query params for non-staff customers.
 * This ensures lists are initially filtered to show only the customer's data.
 */
export function getOrganizationQueryParams(silo: Pick<AuthorizationSilo, "organization" | "systemRole"> | null | undefined): {organizationId: string} | null {
    if (silo?.systemRole) {
        return null
    }
    if (silo?.organization?.id) {
        return {organizationId: silo.organization.id}
    }
    return null
}

/**
 * Application routes configuration with support for parameterized subroutes
 */
export const AppRoutes = {
    // Main navigation routes
    Home: "/pictures",
    Materials: "/materials/library",
    AllMaterials: "/materials",
    Products: "/products",
    Scenes: "/scenes",
    Scanning: "/scanning",
    Tags: "/tags",
    OrganizationGroups: "/organization-groups",
    Organizations: "/organizations",
    Users: "/users",
    Assets: "/assets",
    Hdris: "/hdris",
    RenderJobs: "/jobs",
    RenderNodes: "/render-nodes",
    RenderNodeGroups: "/render-node-groups",
    TaskTypes: "/task-types",
    Files: "/files",
    Models: "/models",
    Statistics: "/statistics",
    Settings: "/settings",
    Templates: "/templates",

    // Create routes
    CreatePicture: "/pictures/create",
    CreateMaterial: "/materials/library/create",
    CreateProduct: "/products/create",
    CreateScene: "/scenes/create",
    CreateTemplate: "/templates/create",
    CreateAsset: "/assets/create",
    CreateHdri: "/hdris/create",
    CreateModel: "/models/create",
    CreateUser: "/users/create",
    CreateTag: "/tags/create",
    CreateRenderNodeGroup: "/render-node-groups/create",
    CreateTaskType: "/task-types/create",
    CreateOrganization: "/organizations/create",
    CreateOrganizationGroup: "/organization-groups/create",

    // Library routes
    MaterialLibrary: "/materials/library",
    MaterialExplorer: "/materials/explorer",

    // Settings subroutes
    OrganizationSettings: "/settings/organization",

    // Scanning subroutes
    ScanningLogs: "/scanning/logs",

    Login: "/login",

    // Parameterized route functions
    PictureItem: (itemId: string) => `/pictures/${itemId}`,
    PictureRevision: (itemId: string, revisionNumber: string) => `/pictures/${itemId}/revisions/${revisionNumber}`,
    PictureView: (itemId: string, revisionNumber: string) => `/pictures/${itemId}/revisions/${revisionNumber}/view`,
    PictureTemplateRevisionAdd: (pictureId: string) => `/pictures/${pictureId}/template-revisions/add`,
    PictureTemplateRevisionEdit: (pictureId: string, templateRevisionId: string) => `/pictures/${pictureId}/template-revisions/${templateRevisionId}/edit`,

    MaterialItem: (materialId: string) => `/materials/${materialId}`,
    MaterialEdit: (materialId: string, revisionId: string) => `/materials/${materialId}/edit/${revisionId}`,
    MaterialSetup: (materialId: string, textureSetRevisionId?: string) =>
        textureSetRevisionId ? `/materials/${materialId}/setup/${textureSetRevisionId}` : `/materials/${materialId}/setup`,
    MaterialPreview: (materialId: string, key: string) => `/materials/${materialId}/preview/${key}`,
    MaterialTasks: (materialId: string, taskId: string) => `/materials/${materialId}/tasks/${taskId}`,
    MaterialSetRevisions: (materialId: string, textureSetRevisionId: string) => `/materials/${materialId}/set-revisions/${textureSetRevisionId}`,

    ProductItem: (templateId: string) => `/products/${templateId}`,
    ProductRevisionsAdd: (templateId: string) => `/products/${templateId}/revisions/add`,
    ProductRevisionsEdit: (templateId: string, templateRevisionId: string) => `/products/${templateId}/revisions/${templateRevisionId}/edit`,
    ProductView: (templateId: string) => `/products/${templateId}/view`,

    SceneItem: (templateId: string) => `/scenes/${templateId}`,
    SceneRevisionsAdd: (templateId: string) => `/scenes/${templateId}/revisions/add`,
    SceneRevisionsEdit: (templateId: string, templateRevisionId: string) => `/scenes/${templateId}/revisions/${templateRevisionId}/edit`,
    SceneView: (templateId: string) => `/scenes/${templateId}/view`,

    TemplateItem: (templateId: string) => `/templates/${templateId}`,
    TemplateRevisionsAdd: (templateId: string) => `/templates/${templateId}/revisions/add`,
    TemplateRevisionsEdit: (templateId: string, templateRevisionId: string) => `/templates/${templateId}/revisions/${templateRevisionId}/edit`,
    TemplateView: (templateId: string) => `/templates/${templateId}/view`,

    TagItem: (tagId: string) => `/tags/${tagId}`,
    OrganizationItem: (organizationId: string) => `/organizations/${organizationId}`,
    UserItem: (userId: string) => `/users/${userId}`,

    AssetItem: (assetId: string) => `/assets/${assetId}`,
    AssetTasks: (assetId: string, taskId: string) => `/assets/${assetId}/tasks/${taskId}`,
    AssetTemplate: (assetId: string, templateId: string) => `/assets/${assetId}/template/${templateId}`,

    HdriItem: (hdriId: string) => `/hdris/${hdriId}`,

    RenderJobItem: (jobId: string) => `/jobs/${jobId}`,
    RenderJobTask: (jobId: string, jobTaskId: string) => `/jobs/${jobId}/${jobTaskId}`,

    FileItem: (fileId: string) => `/files/${fileId}`,

    ModelItem: (modelId: string) => `/models/${modelId}`,
    ModelTasks: (modelId: string, taskId: string) => `/models/${modelId}/tasks/${taskId}`,
} as const

/**
 * Routes where the side navigation should be hidden
 */
export const navigationlessRoutes = [
    "/login",
    "/95ngsj3e/sign-up",
    "/register",
    "/verify-email",
    "/onboarding",
    "/manual-testing",
    "/configurator-components-testing",
    "/sso",
    "/inactive-subscription",
    "/unauthorized",
]
